import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { type Columns, DataTable } from '../components/ui/data-table';
import { Input } from '../components/ui/input';
import { api, errorMessage } from '../lib/api';
import { Button } from '../components/ui/button';
import { can, useMe } from '../lib/session';
import type { CustomerCrm, CustomerDetail, CustomerRow } from '../lib/types';
import { formatDateTime, formatMoney } from '../lib/utils';
import { statusVariant } from './orders/list';

export function CustomersPage() {
  const { t, i18n } = useTranslation();
  const [q, setQ] = useState('');
  const list = useQuery({
    queryKey: ['customers', q.trim()],
    queryFn: () => api<{ items: CustomerRow[] }>(`/customers?q=${encodeURIComponent(q.trim())}`),
  });
  const columns = useMemo<Columns<CustomerRow>>(
    () => [
      {
        id: 'email',
        header: 'Email',
        accessorFn: (r) => r.email,
        cell: ({ row }) => (
          <Link
            to="/customers/$customerId"
            params={{ customerId: row.original.id }}
            className="font-medium hover:underline"
          >
            {row.original.email}
          </Link>
        ),
      },
      { id: 'name', header: t('cust.name'), accessorFn: (r) => r.name },
      {
        id: 'type',
        header: t('cust.type'),
        accessorFn: (r) => r.type,
        cell: ({ row }) => (
          <span className="flex gap-1">
            <Badge variant="outline">{row.original.type}</Badge>
            {row.original.registered ? (
              <Badge variant="secondary">{t('cust.account')}</Badge>
            ) : null}
          </span>
        ),
      },
      { id: 'orders', header: t('cust.orders'), accessorFn: (r) => r.orders },
      {
        id: 'spent',
        header: t('cust.spent'),
        accessorFn: (r) => r.spentMinor,
        cell: ({ row }) => formatMoney(row.original.spentMinor, 'EUR', i18n.language),
      },
      {
        id: 'created',
        header: t('cust.since'),
        accessorFn: (r) => r.createdAt,
        cell: ({ row }) => formatDateTime(row.original.createdAt, i18n.language),
      },
    ],
    [t, i18n.language],
  );
  return (
    <>
      <PageHeader title={t('cust.title')} description={t('cust.hint')} />
      <Input
        aria-label={t('cust.search')}
        placeholder={t('cust.search')}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
        }}
        className="mb-4 w-72"
      />
      {list.error ? (
        <Alert variant="destructive" className="mb-4">
          {errorMessage(list.error)}
        </Alert>
      ) : null}
      <DataTable
        columns={columns}
        data={list.data?.items ?? []}
        loading={list.isPending}
        rowKey={(r) => r.id}
      />
    </>
  );
}

export function CustomerDetailPage({ customerId }: { customerId: string }) {
  const { t, i18n } = useTranslation();
  const customer = useQuery({
    queryKey: ['customers', 'detail', customerId],
    queryFn: () => api<CustomerDetail>(`/customers/${customerId}`),
  });
  const me = useMe();
  const queryClient = useQueryClient();
  const crm = useQuery({
    queryKey: ['customers', 'crm', customerId],
    queryFn: () => api<CustomerCrm>(`/customers/${customerId}/crm`),
  });
  const revoke = async () => {
    await api(`/customers/${customerId}/marketing-consent/revoke`, { method: 'POST' });
    await queryClient.invalidateQueries({ queryKey: ['customers'] });
  };
  const [gdprError, setGdprError] = useState<string | null>(null);
  const [confirmAnonymize, setConfirmAnonymize] = useState(false);
  const exportData = async () => {
    setGdprError(null);
    try {
      const data = await api<unknown>(`/customers/${customerId}/export`);
      const url = URL.createObjectURL(
        new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = `customer-${customerId}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      setGdprError(errorMessage(error));
    }
  };
  const anonymize = async () => {
    setGdprError(null);
    setConfirmAnonymize(false);
    try {
      await api(`/customers/${customerId}/anonymize`, { method: 'POST' });
      await queryClient.invalidateQueries({ queryKey: ['customers'] });
    } catch (error) {
      setGdprError(errorMessage(error));
    }
  };
  if (customer.error) return <Alert variant="destructive">{errorMessage(customer.error)}</Alert>;
  const c = customer.data;
  if (!c) return <p className="text-muted-foreground">{t('common.loading')}</p>;
  return (
    <>
      <PageHeader
        title={[c.firstName, c.lastName].filter(Boolean).join(' ') || c.email}
        description={`${c.email}${c.phone ? ` · ${c.phone}` : ''}`}
      />
      <div className="mb-4 flex flex-wrap gap-2 text-sm">
        <Badge variant="outline">{c.type}</Badge>
        {c.locale ? <Badge variant="outline">{c.locale.toUpperCase()}</Badge> : null}
        <Badge variant={c.marketingConsent ? 'success' : 'secondary'}>
          {c.marketingConsent ? t('cust.marketingYes') : t('cust.marketingNo')}
        </Badge>
        {c.anonymizedAt ? <Badge variant="destructive">{t('cust.anonymized')}</Badge> : null}
      </div>
      {crm.data?.metrics ? (
        <div className="mb-4 flex flex-wrap gap-4 rounded-md border p-3 text-sm">
          <span>
            {t('seg.ltv')}:{' '}
            <b>{formatMoney(crm.data.metrics.revenueMinor, 'EUR', i18n.language)}</b>
          </span>
          <span>
            {t('seg.aov')}: {formatMoney(crm.data.metrics.averageOrderMinor, 'EUR', i18n.language)}
          </span>
          <span>
            {t('seg.lastOrder')}:{' '}
            {crm.data.metrics.lastOrderAt
              ? formatDateTime(crm.data.metrics.lastOrderAt, i18n.language)
              : '—'}
          </span>
          {crm.data.metrics.rfmSegment ? (
            <Badge variant="secondary">
              RFM {crm.data.metrics.recency}-{crm.data.metrics.frequency}-
              {crm.data.metrics.monetary} · {t(`seg.rfm.${crm.data.metrics.rfmSegment as 'new'}`)}
            </Badge>
          ) : null}
          {crm.data.segments.map((s) => (
            <Badge key={s} variant="outline">
              {s}
            </Badge>
          ))}
        </div>
      ) : null}
      {c.marketingConsent && can(me, 'customers.write') ? (
        <Button variant="outline" size="sm" className="mb-4" onClick={() => void revoke()}>
          {t('seg.revoke')}
        </Button>
      ) : null}
      {can(me, 'customers.export') ? (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void exportData()}>
            {t('cust.export')}
          </Button>
          {c.anonymizedAt ? null : confirmAnonymize ? (
            <>
              <span className="text-sm">{t('cust.anonymizeConfirm')}</span>
              <Button variant="destructive" size="sm" onClick={() => void anonymize()}>
                {t('cust.anonymizeYes')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setConfirmAnonymize(false);
                }}
              >
                {t('common.cancel')}
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setConfirmAnonymize(true);
              }}
            >
              {t('cust.anonymize')}
            </Button>
          )}
        </div>
      ) : null}
      {gdprError ? (
        <Alert variant="destructive" className="mb-4">
          {gdprError}
        </Alert>
      ) : null}
      <h2 className="mb-2 text-lg font-semibold">{t('cust.orders')}</h2>
      <ul className="divide-y rounded-md border text-sm">
        {c.orders.map((o) => (
          <li key={o.id} className="flex items-center justify-between gap-3 p-3">
            <Link
              to="/orders/$orderId"
              params={{ orderId: o.id }}
              className="font-medium hover:underline"
            >
              {o.number}
            </Link>
            <span>{formatDateTime(o.createdAt, i18n.language)}</span>
            <Badge variant={statusVariant(o.status)}>
              {t(`ord.statuses.${o.status as 'NEW'}`)}
            </Badge>
            <span className="font-medium">
              {formatMoney(o.totalMinor, o.currency, i18n.language)}
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}
